import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, DateDecoder, EnumDecoder, field, IntegerDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { Formatter } from '@stamhoofd/utility';
import { BalanceItemWithPayments, BalanceItemWithPrivatePayments } from '../BalanceItem';
import { Recipient, Replacement } from '../endpoints/EmailRequest';
import { Payment, PrivatePayment } from '../members/Payment';
import { RecordCheckboxAnswer } from '../members/records/RecordAnswer';
import { downgradePaymentMethodV150, PaymentMethod, PaymentMethodHelper, PaymentMethodV150 } from '../PaymentMethod';
import { PaymentStatus } from '../PaymentStatus';
import { Checkout } from './Checkout';
import { Customer } from './Customer';
import { TicketPrivate } from './Ticket';
import { CheckoutMethodType } from './WebshopMetaData';
export var OrderStatusV103;
(function (OrderStatusV103) {
    OrderStatusV103["Created"] = "Created";
    OrderStatusV103["Prepared"] = "Prepared";
    OrderStatusV103["Completed"] = "Completed";
    OrderStatusV103["Canceled"] = "Canceled";
})(OrderStatusV103 || (OrderStatusV103 = {}));
export var OrderStatusV137;
(function (OrderStatusV137) {
    OrderStatusV137["Created"] = "Created";
    OrderStatusV137["Prepared"] = "Prepared";
    OrderStatusV137["Collect"] = "Collect";
    OrderStatusV137["Completed"] = "Completed";
    OrderStatusV137["Canceled"] = "Canceled";
})(OrderStatusV137 || (OrderStatusV137 = {}));
export var OrderStatus;
(function (OrderStatus) {
    OrderStatus["Created"] = "Created";
    OrderStatus["Prepared"] = "Prepared";
    OrderStatus["Collect"] = "Collect";
    OrderStatus["Completed"] = "Completed";
    OrderStatus["Canceled"] = "Canceled";
    OrderStatus["Deleted"] = "Deleted";
})(OrderStatus || (OrderStatus = {}));
export class OrderStatusHelper {
    static getName(status) {
        switch (status) {
            case OrderStatus.Created: return "Nieuw";
            case OrderStatus.Prepared: return "Verwerkt";
            case OrderStatus.Collect: return "Ligt klaar";
            case OrderStatus.Completed: return "Voltooid";
            case OrderStatus.Canceled: return "Geannuleerd";
            case OrderStatus.Deleted: return "Verwijderd";
        }
    }
    static getColor(status) {
        switch (status) {
            case OrderStatus.Completed: return "success";
            case OrderStatus.Prepared: return "secundary";
            case OrderStatus.Collect: return "tertiary";
            case OrderStatus.Canceled: return "error";
            case OrderStatus.Created: return "info";
        }
        return "error";
    }
}
export class OrderData extends Checkout {
    constructor() {
        super(...arguments);
        this.consumerLanguage = "nl";
        this.comments = "";
        // Payment method is required
        this.paymentMethod = PaymentMethod.Unknown;
    }
    matchQuery(query) {
        const lowerQuery = query.toLowerCase();
        if (this.customer.firstName.toLowerCase().includes(lowerQuery) ||
            this.customer.lastName.toLowerCase().includes(lowerQuery) ||
            this.customer.name.toLowerCase().includes(lowerQuery)) {
            return true;
        }
        // Search product names
        for (const item of this.cart.items) {
            if (item.product.name.toLowerCase().includes(lowerQuery)) {
                return true;
            }
        }
        return false;
    }
    get shouldSendPaymentUpdates() {
        return (!this.timeSlot || (this.timeSlot.date.getTime() + 1000 * 60 * 60 * 24) > new Date().getTime());
    }
    /**
     * Delete the personal data associated with an order when you delete an order.
     * We still need the other data (e.g. to inform other clients about a deleted order)
     * And also to match online payments and handle refunds if needed
     */
    removePersonalData() {
        // Clear customer data
        this.customer = Customer.create({});
        // Clear free inputs
        this.fieldAnswers = [];
        for (const item of this.cart.items) {
            item.fieldAnswers = [];
        }
        this.recordAnswers = [];
    }
}
__decorate([
    field({ decoder: StringDecoder, version: 129 })
], OrderData.prototype, "consumerLanguage", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 158 })
], OrderData.prototype, "comments", void 0);
__decorate([
    field({ decoder: new EnumDecoder(PaymentMethodV150) }),
    field({
        decoder: new EnumDecoder(PaymentMethod),
        version: 151,
        downgrade: downgradePaymentMethodV150
    })
], OrderData.prototype, "paymentMethod", void 0);
export class Order extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.number = null;
        this.data = OrderData.create({});
        this.balanceItems = [];
        /**
         * @deprecated: replaced by balance items
         */
        this.payment = null;
        this.createdAt = new Date();
        this.updatedAt = new Date();
        this.validAt = null;
        this.status = OrderStatus.Created;
    }
    get shouldIncludeStock() {
        return this.status !== OrderStatus.Canceled && this.status !== OrderStatus.Deleted;
    }
    get pricePaid() {
        return this.balanceItems.reduce((total, item) => total + item.pricePaid, 0);
    }
    get totalToPay() {
        if (this.status === OrderStatus.Canceled || this.status === OrderStatus.Deleted) {
            return 0;
        }
        return this.data.totalPrice;
    }
    updatePricePaid() {
        for (const item of this.balanceItems) {
            item.updatePricePaid();
        }
    }
    get payments() {
        return this.balanceItems.flatMap(i => i.payments.map(p => p.payment)).filter(p => p.status !== PaymentStatus.Failed).sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    }
    matchQuery(query) {
        var _a;
        if (this.number + "" == query) {
            return true;
        }
        if ((_a = this.payment) === null || _a === void 0 ? void 0 : _a.matchQuery(query)) {
            return true;
        }
        return this.data.matchQuery(query);
    }
    getHTMLTable() {
        var _a, _b;
        const allFree = this.data.cart.items.every(i => i.getPriceWithoutDiscounts() === 0);
        if (allFree) {
            let str = `<table width="100%" cellspacing="0" cellpadding="0" class="email-data-table"><thead><tr><th>Artikel</th><th>Aantal</th></tr></thead><tbody>`;
            for (const item of this.data.cart.items) {
                str += `<tr><td><h4>${Formatter.escapeHtml(item.product.name)}</h4>${item.description.length > 0 ? "<p style=\"white-space: pre-wrap;\">" + Formatter.escapeHtml(item.description) + "</p>" : ""}</td><td>${Formatter.escapeHtml((_a = item.formattedAmount) !== null && _a !== void 0 ? _a : '1')}</td></tr>`;
            }
            return str + "</tbody></table>";
        }
        let str = `<table width="100%" cellspacing="0" cellpadding="0" class="email-data-table"><thead><tr><th>Artikel</th><th>Aantal</th></tr></thead><tbody>`;
        for (const item of this.data.cart.items) {
            str += `<tr><td><h4>${Formatter.escapeHtml(item.product.name)}</h4>${item.description.length > 0 ? "<p style=\"white-space: pre-wrap;\">" + Formatter.escapeHtml(item.description) + "</p>" : ""}${"<p style=\"white-space: pre-wrap;\">" + Formatter.escapeHtml(item.getFormattedPriceWithDiscount() || item.getFormattedPriceWithoutDiscount()) + "</p>"}</td><td>${Formatter.escapeHtml((_b = item.formattedAmount) !== null && _b !== void 0 ? _b : '1')}</td></tr>`;
        }
        return str + "</tbody></table>";
    }
    getDetailsHTMLTable() {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        let str = `<table width="100%" cellspacing="0" cellpadding="0" class="email-data-table"><tbody>`;
        const data = [
            {
                title: "Bestelnummer",
                value: "" + ((_a = this.number) !== null && _a !== void 0 ? _a : "?")
            },
            {
                title: ((order) => {
                    var _a, _b;
                    if (((_a = order.data.checkoutMethod) === null || _a === void 0 ? void 0 : _a.type) === CheckoutMethodType.Takeout) {
                        return "Afhaallocatie";
                    }
                    if (((_b = order.data.checkoutMethod) === null || _b === void 0 ? void 0 : _b.type) === CheckoutMethodType.OnSite) {
                        return "Locatie";
                    }
                    return "Leveringsadres";
                })(this),
                value: ((order) => {
                    var _a, _b, _c, _d;
                    if (((_a = order.data.checkoutMethod) === null || _a === void 0 ? void 0 : _a.type) === CheckoutMethodType.Takeout) {
                        return order.data.checkoutMethod.name;
                    }
                    if (((_b = order.data.checkoutMethod) === null || _b === void 0 ? void 0 : _b.type) === CheckoutMethodType.OnSite) {
                        return order.data.checkoutMethod.name;
                    }
                    return (_d = (_c = order.data.address) === null || _c === void 0 ? void 0 : _c.shortString()) !== null && _d !== void 0 ? _d : "";
                })(this)
            },
            ...((((_b = this.data.checkoutMethod) === null || _b === void 0 ? void 0 : _b.type) === CheckoutMethodType.Takeout || ((_c = this.data.checkoutMethod) === null || _c === void 0 ? void 0 : _c.type) === CheckoutMethodType.OnSite) && ((_d = this.data.checkoutMethod) === null || _d === void 0 ? void 0 : _d.address) ? [
                {
                    title: 'Adres',
                    value: ((order) => {
                        var _a, _b, _c;
                        return (_c = (_b = (_a = order.data.checkoutMethod) === null || _a === void 0 ? void 0 : _a.address) === null || _b === void 0 ? void 0 : _b.shortString()) !== null && _c !== void 0 ? _c : "";
                    })(this)
                }
            ] : []),
            {
                title: "Datum",
                value: Formatter.capitalizeFirstLetter((_f = (_e = this.data.timeSlot) === null || _e === void 0 ? void 0 : _e.dateString()) !== null && _f !== void 0 ? _f : "")
            },
            {
                title: "Tijdstip",
                value: (_h = (_g = this.data.timeSlot) === null || _g === void 0 ? void 0 : _g.timeRangeString()) !== null && _h !== void 0 ? _h : ""
            },
            {
                title: "Naam",
                value: this.data.customer.name
            },
            ...(this.data.customer.phone ? [
                {
                    title: "GSM-nummer",
                    value: this.data.customer.phone
                }
            ] : []),
            ...this.data.fieldAnswers.filter(a => a.answer).map(a => ({
                title: a.field.name,
                value: a.answer
            })),
            ...this.data.recordAnswers.filter(a => !a.isEmpty || a instanceof RecordCheckboxAnswer).map(a => ({
                title: a.settings.name,
                value: a.stringValue
            })),
            ...((this.data.paymentMethod !== PaymentMethod.Unknown) ? [
                {
                    title: "Betaalmethode",
                    value: Formatter.capitalizeFirstLetter(PaymentMethodHelper.getName(this.data.paymentMethod))
                }
            ] : []),
            ...this.data.priceBreakown.map(p => {
                return {
                    title: p.name,
                    value: Formatter.price(p.price)
                };
            })
        ];
        for (const replacement of data) {
            if (replacement.value.length == 0) {
                continue;
            }
            str += `<tr><td><h4>${Formatter.escapeHtml(replacement.title)}</h4></td><td>${Formatter.escapeHtml(replacement.value)}</td></tr>`;
        }
        return str + "</tbody></table>";
    }
    getRecipient(organization, webshop, payment) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
        const order = this;
        const email = order.data.customer.email.toLowerCase();
        const forcePayment = payment !== null && payment !== void 0 ? payment : order.payment;
        return Recipient.create({
            firstName: order.data.customer.firstName,
            lastName: order.data.customer.lastName,
            email,
            replacements: [
                Replacement.create({
                    token: "firstName",
                    value: (_a = order.data.customer.firstName) !== null && _a !== void 0 ? _a : ""
                }),
                Replacement.create({
                    token: "lastName",
                    value: (_b = order.data.customer.lastName) !== null && _b !== void 0 ? _b : ""
                }),
                Replacement.create({
                    token: "email",
                    value: email
                }),
                Replacement.create({
                    token: "orderUrl",
                    value: "https://" + (webshop === null || webshop === void 0 ? void 0 : webshop.getUrl(organization)) + "/order/" + (order.id)
                }),
                Replacement.create({
                    token: "nr",
                    value: ((_c = order.number) !== null && _c !== void 0 ? _c : "") + ""
                }),
                Replacement.create({
                    token: "orderPrice",
                    value: Formatter.price(order.data.totalPrice)
                }),
                Replacement.create({
                    token: "priceToPay",
                    value: (forcePayment === null || forcePayment === void 0 ? void 0 : forcePayment.status) !== PaymentStatus.Succeeded ? Formatter.price((_d = forcePayment === null || forcePayment === void 0 ? void 0 : forcePayment.price) !== null && _d !== void 0 ? _d : 0) : ""
                }),
                Replacement.create({
                    token: "paymentMethod",
                    value: (forcePayment === null || forcePayment === void 0 ? void 0 : forcePayment.method) ? PaymentMethodHelper.getName(forcePayment.method) : PaymentMethodHelper.getName(this.data.paymentMethod)
                }),
                Replacement.create({
                    token: "transferDescription",
                    value: (forcePayment === null || forcePayment === void 0 ? void 0 : forcePayment.status) !== PaymentStatus.Succeeded && (forcePayment === null || forcePayment === void 0 ? void 0 : forcePayment.method) === PaymentMethod.Transfer ? ((_e = forcePayment === null || forcePayment === void 0 ? void 0 : forcePayment.transferDescription) !== null && _e !== void 0 ? _e : "") : ""
                }),
                Replacement.create({
                    token: "transferBankAccount",
                    value: (forcePayment === null || forcePayment === void 0 ? void 0 : forcePayment.status) !== PaymentStatus.Succeeded && (forcePayment === null || forcePayment === void 0 ? void 0 : forcePayment.method) === PaymentMethod.Transfer ? ((_f = ((webshop === null || webshop === void 0 ? void 0 : webshop.meta.transferSettings.iban) ? webshop === null || webshop === void 0 ? void 0 : webshop.meta.transferSettings.iban : organization.meta.transferSettings.iban)) !== null && _f !== void 0 ? _f : "") : ""
                }),
                Replacement.create({
                    token: "transferBankCreditor",
                    value: (forcePayment === null || forcePayment === void 0 ? void 0 : forcePayment.status) !== PaymentStatus.Succeeded && (forcePayment === null || forcePayment === void 0 ? void 0 : forcePayment.method) === PaymentMethod.Transfer ? ((_g = ((webshop === null || webshop === void 0 ? void 0 : webshop.meta.transferSettings.creditor) ? webshop === null || webshop === void 0 ? void 0 : webshop.meta.transferSettings.creditor : organization.meta.transferSettings.creditor)) !== null && _g !== void 0 ? _g : organization.name) : ""
                }),
                Replacement.create({
                    token: "orderStatus",
                    value: OrderStatusHelper.getName(order.status)
                }),
                Replacement.create({
                    token: "orderMethod",
                    value: (_j = (_h = order.data.checkoutMethod) === null || _h === void 0 ? void 0 : _h.typeName) !== null && _j !== void 0 ? _j : ""
                }),
                Replacement.create({
                    token: "orderLocation",
                    value: ((order) => {
                        var _a, _b, _c, _d;
                        if (((_a = order.data.checkoutMethod) === null || _a === void 0 ? void 0 : _a.type) === CheckoutMethodType.Takeout) {
                            return order.data.checkoutMethod.name;
                        }
                        if (((_b = order.data.checkoutMethod) === null || _b === void 0 ? void 0 : _b.type) === CheckoutMethodType.OnSite) {
                            return order.data.checkoutMethod.name;
                        }
                        return (_d = (_c = order.data.address) === null || _c === void 0 ? void 0 : _c.shortString()) !== null && _d !== void 0 ? _d : "";
                    })(order)
                }),
                Replacement.create({
                    token: "orderDate",
                    value: (_l = (_k = order.data.timeSlot) === null || _k === void 0 ? void 0 : _k.dateString()) !== null && _l !== void 0 ? _l : ""
                }),
                Replacement.create({
                    token: "orderTime",
                    value: (_o = (_m = order.data.timeSlot) === null || _m === void 0 ? void 0 : _m.timeRangeString()) !== null && _o !== void 0 ? _o : ""
                }),
                Replacement.create({
                    token: "orderDetailsTable",
                    value: "",
                    html: order.getDetailsHTMLTable()
                }),
                Replacement.create({
                    token: "orderTable",
                    value: "",
                    html: order.getHTMLTable()
                }),
                Replacement.create({
                    token: "paymentTable",
                    value: "",
                    html: forcePayment === null || forcePayment === void 0 ? void 0 : forcePayment.getHTMLTable()
                }),
                Replacement.create({
                    token: "organizationName",
                    value: organization.name
                }),
                Replacement.create({
                    token: "webshopName",
                    value: webshop.meta.name
                }),
            ]
        });
    }
}
__decorate([
    field({ decoder: StringDecoder })
], Order.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 140 })
], Order.prototype, "webshopId", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true })
], Order.prototype, "number", void 0);
__decorate([
    field({ decoder: OrderData })
], Order.prototype, "data", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(BalanceItemWithPayments), version: 225 })
], Order.prototype, "balanceItems", void 0);
__decorate([
    field({ decoder: Payment, nullable: true })
], Order.prototype, "payment", void 0);
__decorate([
    field({ decoder: DateDecoder })
], Order.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: DateDecoder, version: 107 })
], Order.prototype, "updatedAt", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], Order.prototype, "validAt", void 0);
__decorate([
    field({ decoder: new EnumDecoder(OrderStatusV103), version: 47 })
    // Migrate newer order status .collect in case of older client
    ,
    field({
        decoder: new EnumDecoder(OrderStatusV137),
        version: 104,
        upgrade: (old) => {
            return old;
        },
        downgrade: (n) => {
            if (n === OrderStatusV137.Collect) {
                // Map to other status
                return OrderStatusV103.Prepared;
            }
            return n;
        }
    }),
    field({
        decoder: new EnumDecoder(OrderStatus),
        version: 138,
        upgrade: (old) => {
            return old;
        },
        downgrade: (n) => {
            if (n === OrderStatus.Deleted) {
                // Map to other status
                return OrderStatusV137.Canceled;
            }
            return n;
        }
    })
], Order.prototype, "status", void 0);
export class PrivateOrder extends Order {
    constructor() {
        super(...arguments);
        /**
         * @deprecated
         */
        this.payment = null;
        this.balanceItems = [];
    }
    get payments() {
        return this.balanceItems.flatMap(i => i.payments.map(p => p.payment)).filter(p => p.status !== PaymentStatus.Failed).sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    }
}
__decorate([
    field({ decoder: PrivatePayment, nullable: true })
], PrivateOrder.prototype, "payment", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(BalanceItemWithPrivatePayments), nullable: true, version: 225 })
], PrivateOrder.prototype, "balanceItems", void 0);
export class PrivateOrderWithTickets extends PrivateOrder {
    constructor() {
        super(...arguments);
        this.tickets = [];
    }
    /**
     * Adds or removes tickets as appropriate
     */
    addTickets(tickets) {
        for (const ticket of tickets) {
            if (ticket.orderId == this.id) {
                if (ticket.deletedAt) {
                    const existingIndex = this.tickets.findIndex(t => t.id === ticket.id);
                    if (existingIndex !== -1) {
                        this.tickets.splice(existingIndex, 1);
                    }
                }
                else {
                    const existing = this.tickets.find(t => t.id === ticket.id);
                    if (existing) {
                        existing.set(ticket);
                    }
                    else {
                        this.tickets.push(ticket);
                    }
                }
            }
        }
    }
    addTicketPatches(patches) {
        PrivateOrderWithTickets.addTicketPatches([this], patches);
    }
    static addTicketPatches(orders, patches) {
        mainLoop: for (const patch of patches) {
            for (const order of orders) {
                for (const ticket of order.tickets) {
                    if (ticket.id === patch.id) {
                        ticket.set(ticket.patch(patch));
                        continue mainLoop;
                    }
                }
            }
        }
    }
}
__decorate([
    field({ decoder: new ArrayDecoder(TicketPrivate) })
], PrivateOrderWithTickets.prototype, "tickets", void 0);
export class OrderResponse extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.paymentUrl = null;
        /**
         * Data to put in a QR-code
         */
        this.paymentQRCode = null;
    }
}
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], OrderResponse.prototype, "paymentUrl", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, optional: true })
], OrderResponse.prototype, "paymentQRCode", void 0);
__decorate([
    field({ decoder: Order })
], OrderResponse.prototype, "order", void 0);
//# sourceMappingURL=Order.js.map